import { Send } from "lucide-react";
import { useState, type FormEvent } from "react";
import { newMutationId, type Locale } from "@vzlive/shared";
import { t } from "@vzlive/shared/i18n";
import { apiFetch } from "../lib/api";

export function SubmissionForm({
  eventId,
  locale,
  compact = false,
}: {
  eventId: string;
  locale: Locale;
  compact?: boolean;
}) {
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const element = event.currentTarget;
    const form = new FormData(element);
    setState("sending");
    try {
      await apiFetch("/api/public/submissions", {
        method: "POST",
        body: JSON.stringify({
          eventId,
          displayName: form.get("name"),
          email: form.get("email") || undefined,
          content: form.get("content"),
          consent: form.get("consent") === "on",
          clientMutationId: newMutationId(),
        }),
      });
      setState("sent");
      element.reset();
    } catch {
      setState("error");
    }
  };

  return (
    <section className="submission-section">
      <div>
        {!compact && <h2>{t(locale, "submissionTitle")}</h2>}
        <p>{t(locale, "submissionDescription")}</p>
      </div>
      {state === "sent" ? (
        <p
          className="submission-success"
          role="status"
          tabIndex={-1}
          ref={(node) => node?.focus()}
        >
          {t(locale, "submitted")}
        </p>
      ) : (
        <form onSubmit={submit} aria-busy={state === "sending"}>
          <label>
            <span>{t(locale, "submissionName")}</span>
            <input name="name" required maxLength={100} autoComplete="name" />
          </label>
          <label>
            <span>
              {t(locale, "email")} <small>(optional)</small>
            </span>
            <input name="email" type="email" autoComplete="email" />
          </label>
          <label className="wide">
            <span>{t(locale, "submissionMessage")}</span>
            <textarea
              name="content"
              required
              minLength={2}
              maxLength={5000}
              rows={4}
            />
          </label>
          <label className="check wide">
            <input type="checkbox" name="consent" required />
            <span>{t(locale, "submissionConsent")}</span>
          </label>
          <button type="submit" disabled={state === "sending"}>
            {t(locale, "submit")} <Send size={18} aria-hidden="true" />
          </button>
          {state === "error" && (
            <p role="alert">
              {locale === "en"
                ? "Something went wrong. Please try again."
                : "Das hat nicht geklappt. Bitte versuche es erneut."}
            </p>
          )}
        </form>
      )}
    </section>
  );
}
